<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Tempo de Resposta por Canal',
  },
});

const sortedData = computed(() => {
  if (!props.data || props.data.length === 0) return [];
  return [...props.data].sort((a, b) => a.avg_minutes - b.avg_minutes);
});

const avgResponseTime = computed(() => {
  if (sortedData.value.length === 0) return 0;
  const total = sortedData.value.reduce((sum, i) => sum + i.avg_minutes, 0);
  return total / sortedData.value.length;
});

const maxTime = computed(() => {
  if (sortedData.value.length === 0) return 1;
  return Math.max(...sortedData.value.map(i => i.avg_minutes), 1);
});

function formatTime(minutes) {
  if (!minutes || minutes === 0) return '0 min';
  if (minutes < 1) return `${(minutes * 60).toFixed(0)}s`;
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  return `${(minutes / 60).toFixed(1)}h`;
}

function getTimeColor(minutes) {
  if (minutes <= 5) return 'bg-n-teal-9';
  if (minutes <= 15) return 'bg-n-blue-9';
  if (minutes <= 30) return 'bg-n-amber-9';
  return 'bg-n-ruby-9';
}

function getTimeTextColor(minutes) {
  if (minutes <= 5) return 'text-n-teal-11';
  if (minutes <= 15) return 'text-n-blue-11';
  if (minutes <= 30) return 'text-n-amber-11';
  return 'text-n-ruby-11';
}

function getChannelIcon(channelType) {
  const icons = {
    'Channel::WebWidget': 'i-lucide-globe',
    'Channel::Api': 'i-lucide-code',
    'Channel::Whatsapp': 'i-lucide-message-circle',
    'Channel::FacebookPage': 'i-lucide-facebook',
    'Channel::TwitterProfile': 'i-lucide-twitter',
    'Channel::Email': 'i-lucide-mail',
    'Channel::Telegram': 'i-lucide-send',
  };
  return icons[channelType] || 'i-lucide-inbox';
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-timer text-lg text-n-blue-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-xs text-n-slate-11">Media:</span>
        <span
          class="text-lg font-bold"
          :class="getTimeTextColor(avgResponseTime)"
        >
          {{ formatTime(avgResponseTime) }}
        </span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-64">
      <Spinner size="large" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="sortedData.length === 0"
      class="flex flex-col items-center justify-center h-64 text-n-slate-11"
    >
      <span class="i-lucide-clock text-4xl mb-2 opacity-50" />
      <span class="text-sm">Sem dados de tempo de resposta</span>
    </div>

    <!-- Chart -->
    <div v-else class="flex flex-col gap-3 max-h-80 overflow-y-auto">
      <div
        v-for="(inbox, index) in sortedData"
        :key="inbox.inbox_id"
        class="flex items-center gap-3 p-2 rounded-lg bg-n-alpha-1"
      >
        <span
          class="text-sm font-medium w-5"
          :class="index < 3 ? 'text-n-teal-11' : 'text-n-slate-10'"
        >
          {{ index + 1 }}
        </span>

        <span
          :class="getChannelIcon(inbox.channel_type)"
          class="text-lg text-n-slate-11"
        />

        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between mb-1">
            <span class="text-sm font-medium text-n-slate-12 truncate">{{
              inbox.inbox_name
            }}</span>
            <span
              class="text-lg font-bold"
              :class="getTimeTextColor(inbox.avg_minutes)"
            >
              {{ formatTime(inbox.avg_minutes) }}
            </span>
          </div>

          <div class="flex items-center gap-2">
            <div class="flex-1 h-2 bg-n-alpha-2 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full transition-all duration-300"
                :class="getTimeColor(inbox.avg_minutes)"
                :style="{
                  width: `${Math.min((inbox.avg_minutes / maxTime) * 100, 100)}%`,
                }"
              />
            </div>
          </div>

          <div class="flex items-center gap-3 mt-1 text-xs text-n-slate-10">
            <span>Mediana: {{ formatTime(inbox.median_minutes) }}</span>
            <span>P90: {{ formatTime(inbox.p90_minutes) }}</span>
            <span class="ml-auto">{{ inbox.total_responses }} respostas</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Legend -->
    <div
      class="flex items-center justify-center gap-4 mt-4 pt-3 border-t border-n-weak text-xs"
    >
      <div class="flex items-center gap-1">
        <span class="w-2 h-2 rounded bg-n-teal-9" />
        <span class="text-n-slate-11">&lt;5min</span>
      </div>
      <div class="flex items-center gap-1">
        <span class="w-2 h-2 rounded bg-n-blue-9" />
        <span class="text-n-slate-11">&lt;15min</span>
      </div>
      <div class="flex items-center gap-1">
        <span class="w-2 h-2 rounded bg-n-amber-9" />
        <span class="text-n-slate-11">&lt;30min</span>
      </div>
      <div class="flex items-center gap-1">
        <span class="w-2 h-2 rounded bg-n-ruby-9" />
        <span class="text-n-slate-11">&gt;30min</span>
      </div>
    </div>
  </div>
</template>
